<template>
  <div>
    <h3 v-dompurify-html="newsletter.name" />
    <template v-if="showSubscriptionMessage">
      <Message class="mb-2" role="alert" :severity="subscriptionMessage.severity">
        {{ verificationMessage ? verificationMessage : subscriptionMessage.message }}
      </Message>
    </template>
    <div>
      <Form @submit="onSubscribe">
        <InfoRequiredFields />
        <div v-if="showForm">
          <form-control-input
            :required="true"
            ref="emailInput"
            name="email"
            code="email"
            type="email"
            class="w-50"
            placeholder="E-Mail-Adresse"
            label="Bitte geben Sie Ihre e-Mail-Adresse an."
          />
          <Captcha
            ref="captchaComponent"
            v-if="hasCaptcha"
            :serverError="captchaError"
            v-on:captcha-updated="onCaptchaUpdated"
          ></Captcha>
          <br />
          <form-control-checkbox
            :name="'privacy_policy_' + newsletter.id"
            :required="true"
            :label="privacyFieldLabel"
            :public="true"
            error="Um sich anzumelden, müssen Sie der Datenschutzerklärung zustimmen."
            :hidden="false"
          />
        </div>
        <br />
        <button v-if="showSubscribeButton" class="btn btn-primary" type="submit">Newsletter abonnieren</button>
      </Form>
    </div>
    <button v-if="isSubscribed && isLoggedIn" class="btn btn-primary" @click="onUnSubscribe">
      vom Newsletter abmelden
    </button>
  </div>
</template>

<script>
import { defineComponent, ref, watch, computed, onMounted, reactive } from 'vue';

import { domainStore } from '@/store/domain-store';
import { userStore } from '@/store/user-store';
import { newsletterStore } from '@/store/newsletter-store';

import newsletterRepository from '@/api/newsletterRepository';
import useConverter from '@/composables/general/useConverter';

import { Form } from 'vee-validate';
import FormControlInput from '@/components/general/FormControl/Input';
import FormControlCheckbox from '@/components/general/FormControl/Checkbox';
import Captcha from '@/components/general/Captcha.vue';
import Message from 'primevue/message';
import InfoRequiredFields from '../general/InfoRequiredFields.vue';

export default defineComponent({
  name: 'NewsletterDetail',

  props: {
    newsletter: {
      type: Object,
      required: true,
    },
    verificationMessage: {
      type: String,
      required: false,
      default: '',
    },
    privacyPageUri: {
      type: String,
      required: false,
    },
  },

  setup(props) {
    const { toBool } = useConverter();

    //vars
    const email = ref();
    const isSubscribed = ref(false);
    const showSubscribeButton = ref(true);
    const captchaId = ref(0);
    const captchaError = ref('');
    const captchaComponent = ref(null); // Reference to captcha component
    const emailInput = ref(null); // Reference to email input field

    const subscriptionMessage = reactive({
      message: '',
      type: 'success',
    });

    //computed
    const user = computed(() => {
      return userStore.getState().user;
    });
    const domain = computed(() => {
      return domainStore.getState().domain;
    });
    const isLoggedIn = computed(() => {
      return userStore.isLoggedIn();
    });
    const showSubscriptionMessage = computed(() => {
      return props.verificationMessage || subscriptionMessage.message ? true : false;
    });
    const showForm = computed(() => {
      return (
        !isLoggedIn.value &&
        ((!isSubscribed.value && !subscriptionMessage.message && !props.verificationMessage) ||
          showSubscribeButton.value)
      );
    });
    const hasCaptcha = computed(() => {
      return toBool(domain.value.general_config.DomainEnableNewsletterCaptcha);
    });
    const privacyFieldLabel = computed(() => {
      if (props.privacyPageUri && props.privacyPageUri !== '') {
        return (
          "Ich habe die <a href='" +
          props.privacyPageUri +
          "' target='_blank'>Datenschutzerklärung</a> gelesen und erkläre mich mit dieser einverstanden."
        );
      }
      return 'Ich habe die Datenschutzerklärung gelesen und erkläre mich mit dieser einverstanden.';
    });
    //methods
    const onCaptchaUpdated = captcha_object => {
      captchaId.value = captcha_object.captcha_id;
    };
    const onSubscribe = values => {
      let email;
      if (user.value && user.value.virtual !== 1) {
        email = user.value.email;
      } else {
        email = values.email;
      }

      newsletterStore
        .subscribe(props.newsletter.id, email, captchaId.value, values['captcha'] || '')
        .then(response => {
          subscriptionMessage.message = response;
          showSubscribeButton.value = false;
          isSubscribed.value = true;
        })
        .catch(err => {
          if (err.type == 'captcha') {
            captchaComponent.value.focusCaptchaField();
            captchaError.value = err.message;
          } else {
            emailInput.value.focus();
          }
          showSubscribeButton.value = true;
          isSubscribed.value = false;
        });
    };
    const onUnSubscribe = () => {
      newsletterRepository
        .unsubscribe_user(props.newsletter.id)
        .then(() => {
          subscriptionMessage.message = 'Sie haben sich erfolgreich vom Newsletter abgemeldet';
          isSubscribed.value = false;
          showSubscribeButton.value = true;
        })
        .catch(() => {
          subscriptionMessage.message = 'Ein Fehler ist aufgetreten, bitte versuchen Sie es erneut';
          subscriptionMessage.severity = 'warn';
        });
    };

    const getSubscription = () => {
      newsletterStore
        .getSubscription(props.newsletter.id)
        .then(responseMessage => {
          subscriptionMessage.message = responseMessage;
          subscriptionMessage.severity = 'info';
          showSubscribeButton.value = false;
          isSubscribed.value = true;
        })
        .catch(errorMessage => {
          console.warn(errorMessage);
          subscriptionMessage.message = null;
          subscriptionMessage.severity = 'success';
          showSubscribeButton.value = true;
          isSubscribed.value = false;
        });
    };
    onMounted(() => {
      if (isLoggedIn.value) {
        getSubscription();
      } else {
        subscriptionMessage.message = null;
        subscriptionMessage.severity = 'success';
        showSubscribeButton.value = true;
        email.value = null;
        return;
      }
    });
    //lifehooks
    watch(
      () => isLoggedIn.value,
      (nV, oV) => {
        if (nV != oV && user.value) {
          getSubscription();
        } else {
          subscriptionMessage.message = null;
          subscriptionMessage.severity = 'success';
          showSubscribeButton.value = true;
          email.value = null;
          return;
        }
      },
      { deep: true }
    );

    // if unsubscribed via url/token, change isSubscribed value
    watch(
      () => props.verificationMessage,
      () => {
        if (props.verificationMessage == 'Sie haben sich erfolgreich vom Newsletter abgemeldet!') {
          showSubscribeButton.value = true;
          isSubscribed.value = false;
        }
      },
      { deep: true }
    );
    return {
      email,
      captchaError,
      onSubscribe,
      onUnSubscribe,
      onCaptchaUpdated,
      captchaComponent,
      emailInput,
      hasCaptcha,
      subscriptionMessage,
      isSubscribed,
      showSubscriptionMessage,
      showSubscribeButton,
      showForm,
      isLoggedIn,
      privacyFieldLabel,
    };
  },

  components: {
    InfoRequiredFields,
    FormControlInput,
    FormControlCheckbox,
    Form,
    Captcha,
    Message,
  },
});
</script>

<style scoped>
.subscription-message {
  border: 1px solid #eeeeee;
  background-color: #f9f9f9;
}
</style>
